<template lang="pug">
  yz-dialog(v-if="showDialog" v-model="showDialog" fullscreen persistent scrollable)
    yz-card
      yz-dialog-fullscreen-header(title="Assign balance" :loading="isLoading" @back="closeDialog")
        template(#actions)
          .d-flex.align-center(style="gap: 12px")
            yz-btn(size="lg" text label="Cancel" :disabled="isLoading" @click="closeDialog")
            yz-tooltip(:text="saveTooltipText" :disabled="!isSaveDisabled" direction="left")
              yz-btn(size="lg" label="Save" :loading="isLoading" :disabled="isSaveDisabled" @click="saveChanges")
      yz-dialog-fullscreen-content.pa-6
        // Loading state
        template(v-if="isFetching")
          .d-flex.justify-center.align-center.py-12
            v-progress-circular(indeterminate color="primary")

        // Error state
        template(v-else-if="isError")
          yz-empty-state-card(icon="$yzi-alert-triangle" title="Unable to load invoices" description="The invoice information could not be retrieved. Please check and try again." border-color="yz-danger-500" icon-color="yz-danger-500" action-label="Try again" @action-click="fetchAllInvoices")

        // Main content
        template(v-else)
          // Header Summary Section
          yz-card.pa-3.mb-6(color="yz-neutral-300" outlined style="border-radius: 10px;")
            v-row(no-gutters align="center")
              v-col(cols="auto")
                .d-flex.align-center.gap-2.mb-2
                  .yz-medium-body-sm.text-neutral-700 {{ parentClaim.panel.name }}
                  yz-divider(vertical color="yz-neutral-400")
                  .yz-medium-body-xs.text-neutral-500 Claim {{ '#' + parentClaim.rid }}
                .yz-heading-6.text-neutral-700 {{ totalAmount | displayPrice($currency) }}

              v-spacer

              v-col(cols="auto")
                .text-right
                  .yz-medium-body-sm.text-neutral-700.mb-1 Total approved amount
                  .yz-heading-6.text-neutral-700 {{ approvedAmount | displayPrice($currency) }}

              v-col.ml-5(cols="auto" style="border-left: 1px solid #C1C1C1; padding-left: 20px;")
                .text-right
                  .yz-medium-body-sm.text-neutral-700.mb-1 Outstanding amount
                  .yz-heading-6(:class="outstandingAmount === 0 ? 'text-success' : 'text-danger'") {{ outstandingAmount | displayPrice($currency) }}

          // Claims Table
          yz-card(outlined)
            .pa-4
              yz-data-table(:headers="tableHeaders" :items="invoicesData" :items-per-page="pageSize" :page="currentPage" :loading="isFetching" disable-sort)
                template(v-slot:item.amount="{ item }")
                  .yz-medium-body-sm {{ item.amount | displayPrice($currency) }}

                template(v-slot:item.invoiceNo="{ item }")
                  .yz-medium-body-sm {{ item.invoiceNo }}

                template(v-slot:item.approvedDate="{ item }")
                  .yz-medium-body-sm
                    span(v-if="item.approvedDate") {{ item.approvedDate }}
                    span.text-neutral-500(v-else) N/A

                template(v-slot:item.approvedAmount="{ item }")
                  yz-text-field.my-2(v-model="item.approvedAmount" size="md" hide-details, readonly, background-color="yz-neutral-200")

                template(v-slot:item.rejectedAmount="{ item }")
                  yz-text-field(v-model.number="item.rejectedAmount" type="number" min="0" size="md" :prefix="$currency.symbol" :rules="getRejectedAmountRules(item)" required required-error-message="Rejected amount is required")

              // Pagination
              .d-flex.justify-end.mt-4
                yz-pagination(v-model="currentPage" :page-size="pageSize" :count="invoicesData.length" type="page" arrow)
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import { validateDecimalPlaces, hasExcessiveDecimals } from '@/utils'

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    parentClaim: {
      type: Object,
      default: null
    },
    rejectedAmount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isLoading: false,
      isFetching: true,
      isError: false,
      currentPage: 1,
      pageSize: 20,

      // Table headers
      tableHeaders: [
        { text: 'Amount', value: 'amount', width: '200px' },
        { text: 'Invoice no', value: 'invoiceNo', width: '200px' },
        { text: 'Approved date', value: 'approvedDate', width: '200px' },
        { text: 'Approved amount', value: 'approvedAmount', width: '200px' },
        { text: 'Rejected amount', value: 'rejectedAmount', width: '200px' }
      ],

      // Invoice data from API
      invoicesData: []
    }
  },
  mounted() {
    if (this.parentClaim && this.parentClaim.invoices && this.parentClaim.invoices.length > 0) {
      this.fetchAllInvoices()
    } else {
      this.isError = true
      this.isFetching = false
    }
  },
  watch: {
    // Recalculate approved amounts when rejected amounts change
    invoicesData: {
      handler() {
        this.invoicesData.forEach(invoice => {
          const rejectedAmountInCents = invoice.rejectedAmount != null && invoice.rejectedAmount !== '' ? Math.round(parseFloat(invoice.rejectedAmount) * this.$currency.multiplier) : 0
          const currentAmountInCents = parseFloat(invoice.amount || 0)
          // Calculate approved amount = invoice amount - rejected amount (in display format)
          const approvedInCents = Math.max(0, currentAmountInCents - rejectedAmountInCents)
          invoice.approvedAmount = this.$options.filters.displayPrice(approvedInCents, this.$currency)
        })
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    totalAmount() {
      // Sum of all invoice amounts (already in cents from API)
      return this.invoicesData.reduce((sum, invoice) => sum + parseFloat(invoice.amount || 0), 0)
    },
    totalRejectedAmount() {
      // Sum of all rejected amounts entered by user (convert to cents)
      return this.invoicesData.reduce((sum, invoice) => {
        const rejected = invoice.rejectedAmount != null && invoice.rejectedAmount !== '' ? Math.round(parseFloat(invoice.rejectedAmount) * this.$currency.multiplier) : 0
        return sum + rejected
      }, 0)
    },
    approvedAmount() {
      // Sum of all approved amounts (calculated: invoice amount - rejected amount)
      return this.totalAmount - this.totalRejectedAmount
    },
    outstandingAmount() {
      // Calculate outstanding: parent's rejected amount - sum of user's rejected amounts
      // This should be zero when user has properly distributed all rejected amounts
      const rejectedAmountInCents = this.rejectedAmount || 0
      return rejectedAmountInCents - this.totalRejectedAmount
    },
    isSaveDisabled() {
      // Check 1: All rejected amounts must be filled (not null/empty)
      const hasEmptyAmount = this.invoicesData.some(invoice => {
        const amount = invoice.rejectedAmount
        return amount == null || amount === ''
      })

      // Check 2: All rejected amounts must be >= 0 (no negative values)
      const hasNegativeAmount = this.invoicesData.some(invoice => {
        return parseFloat(invoice.rejectedAmount) < 0
      })

      // Check 3: Rejected amount must not exceed invoice amount
      const hasOverRejection = this.invoicesData.some(invoice => {
        const rejectedInCents = Math.round(parseFloat(invoice.rejectedAmount || 0) * this.$currency.multiplier)
        const invoiceAmountInCents = parseFloat(invoice.amount || 0)
        return rejectedInCents > invoiceAmountInCents
      })

      // Check 4: Rejected amount has too many decimal places
      const hasExcessiveDecimalsCheck = this.invoicesData.some(invoice => hasExcessiveDecimals(invoice.rejectedAmount, this.$currency))

      // Check 5: Outstanding amount must be zero
      const hasOutstandingAmount = this.outstandingAmount !== 0

      return this.isFetching || hasEmptyAmount || hasNegativeAmount || hasOverRejection || hasExcessiveDecimalsCheck || hasOutstandingAmount
    },
    saveTooltipText() {
      // Check for empty amounts
      const hasEmptyAmount = this.invoicesData.some(invoice => {
        const amount = invoice.rejectedAmount
        return amount == null || amount === ''
      })

      if (hasEmptyAmount) {
        return 'Please fill in all rejected amounts'
      }

      // Check for negative amounts
      const hasNegativeAmount = this.invoicesData.some(invoice => {
        return parseFloat(invoice.rejectedAmount) < 0
      })

      if (hasNegativeAmount) {
        return 'All rejected amounts must be 0 or greater'
      }

      // Check for over-rejection (rejected > invoice amount)
      const hasOverRejection = this.invoicesData.some(invoice => {
        const rejectedInCents = Math.round(parseFloat(invoice.rejectedAmount || 0) * this.$currency.multiplier)
        const invoiceAmountInCents = parseFloat(invoice.amount || 0)
        return rejectedInCents > invoiceAmountInCents
      })

      if (hasOverRejection) {
        return 'Rejected amounts cannot exceed invoice amounts'
      }

      // Check for excessive decimal places
      const hasExcessiveDecimalsCheck = this.invoicesData.some(invoice => hasExcessiveDecimals(invoice.rejectedAmount, this.$currency))

      if (hasExcessiveDecimalsCheck) {
        return `Maximum ${this.$currency.exponent} decimal places allowed`
      }

      // Check for outstanding amount
      if (this.outstandingAmount !== 0) {
        return 'Outstanding amount must be zero to proceed'
      }

      return ''
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    getRejectedAmountRules(item) {
      // Convert invoice amount from cents to display format for comparison
      const maxRejectedAmount = parseFloat(item.amount || 0) / this.$currency.multiplier

      return [v => (v != null && v !== '') || 'Rejected amount is required', v => parseFloat(v) >= 0 || 'Amount must be 0 or greater', v => parseFloat(v) <= maxRejectedAmount || `Cannot exceed ${this.$options.filters.displayPrice(item.amount, this.$currency)}`, v => validateDecimalPlaces(v, this.$currency)]
    },
    sanitizeData(invoice) {
      // Format approved date from parent claim
      let approvedDate = this.parentClaim.approved_date || null
      if (this.parentClaim && this.parentClaim.approved_date) {
        try {
          const dt = DateTime.fromISO(this.parentClaim.approved_date)
          approvedDate = dt.toFormat('dd MMMM yyyy')
        } catch (error) {
          console.error('Error formatting date:', error)
          approvedDate = this.parentClaim.approved_date
        }
      }

      return {
        uid: invoice.uid,
        amount: invoice.amount, // Keep in cents
        invoiceNo: invoice.invoice_no?.toString() || '',
        approvedDate,
        approvedAmount: this.$options.filters.displayPrice(invoice.amount, this.$currency), // Will be recalculated by watcher
        rejectedAmount: 0,
        originalInvoice: invoice
      }
    },
    async fetchAllInvoices() {
      this.isFetching = true
      this.isError = false

      try {
        // Extract invoice UIDs from parentClaim.invoices
        const invoiceUids = this.parentClaim.invoices.join(',')

        // Get panel ID from parentClaim
        const panelId = this.parentClaim?.panel?.id

        const params = {
          uids: invoiceUids,
          page_size: -1, // Fetch all invoices
          panel: panelId,
          fields: ['uid', 'invoice_no', 'amount']
        }

        const { data } = await this.$axios.get(`/panels/${this.company.slug}/invoices`, { params })

        // Transform and store the fetched invoice data
        if (data.count > 0) {
          this.invoicesData = data.results.map(invoice => this.sanitizeData(invoice))
        } else {
          this.isError = true
          console.error('No invoices found for the selected UIDs.')
          await this.showMessage(['error', 'No invoices found for the selected items. Please check and try again.'])
          this.invoicesData = []
        }
      } catch (error) {
        console.error('Error fetching invoices:', error)
        this.isError = true
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    closeDialog() {
      this.$emit('input', false)
      this.$emit('cancel')
    },

    saveChanges() {
      // Validate that outstanding amount is zero (total rejected amounts must equal parent's rejected amount)
      if (this.outstandingAmount !== 0) {
        this.showMessage(['error', 'Total rejected amounts must equal the outstanding amount to proceed'])
        return
      }

      this.isLoading = true

      // Format payload for parent component
      const invoicesPayload = this.invoicesData.map(invoice => {
        const rejectedInCents = Math.round(parseFloat(invoice.rejectedAmount || 0) * this.$currency.multiplier)
        const invoiceAmountInCents = parseFloat(invoice.amount || 0)
        const approvedInCents = Math.max(0, invoiceAmountInCents - rejectedInCents)

        return {
          uid: invoice.uid,
          amount: approvedInCents, // In cents
          rejected_amount: rejectedInCents // In cents
        }
      })

      const finalData = {
        invoices: invoicesPayload,
        totalApprovedAmount: this.approvedAmount,
        totalRejectedAmount: this.totalRejectedAmount
      }

      this.$emit('confirmed', finalData)
      this.isLoading = false
      this.$emit('input', false)
    }
  }
}
</script>

<style scoped></style>
